import type { HomeyAPI, HomeyAPIV3Cloud } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

type LanguageOption = Awaited<ReturnType<HomeyAPI['i18n']['getOptionLanguage']>>;

export interface State {
  optionLanguageData: LanguageOption | null;
  isLoading: boolean;
  error: unknown;
}

export type LanguageSettingsStoreState = State;

export class LanguageSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('language-settings');
  }

  override createInitialState() {
    return {
      optionLanguageData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      const optionLanguageData = await api.i18n.getOptionLanguage();

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        optionLanguageData,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, this.key);

    return { languageSettings: this.store(selectAll, shallow) };
  }

  async setOptionLanguage({ value }: HomeyAPIV3Cloud.ManagerI18n.SetOptionLanguageOptions) {
    const api = this.getApi();
    const result = await api.i18n.setOptionLanguage({ value });
    this.set({ optionLanguageData: result });
  }
}

function selectAll(state: LanguageSettingsStoreState) {
  return {
    ...selectBase(state),
    optionLanguageData: state.optionLanguageData,
  };
}

export const languageSettingsStore = new LanguageSettingsStore();
